import { size } from "@mesketch/design-tokens";
import { ProgressBar } from "@mesketch/svg";
import { useEffect, useRef } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import type { RatingFormat } from "../data/api.js";
import { T, useTexts } from "./texts.js";
import { useTone } from "./tone.js";

// Obrazovka položky (REQ-020, DES-003 §7): jedno tvrzení velkým písmem, pod ním odpovědi
// formátu `rating` s rostoucími kolečky a popisky z `labels_key`. Klávesy 1 až 5 vyberou
// odpověď. Skupina odpovědí má sémantiku skupiny přepínačů.

export interface ItemScreenProps {
  readonly itemId: string;
  readonly format: RatingFormat;
  readonly round: number;
  readonly rounds: number;
  readonly index: number;
  readonly count: number;
  readonly selected: number | null;
  readonly instructionsKey: string | null;
  readonly onAnswer: (value: number) => void;
  readonly onBack: (() => void) | null;
  readonly onLater: () => void;
}

export function ItemScreen({ itemId, format, round, rounds, index, count, selected, instructionsKey, onAnswer, onBack, onLater }: ItemScreenProps) {
  const intl = useIntl();
  const { text } = useTexts();
  const values = Array.from({ length: format.max - format.min + 1 }, (_, i) => format.min + i);
  const answer = useRef(onAnswer);
  answer.current = onAnswer;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      const n = Number(e.key);
      if (Number.isInteger(n) && n >= format.min && n <= format.max && e.key.length === 1) {
        e.preventDefault();
        answer.current(n);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [format.min, format.max]);

  const statementId = `statement-${itemId}`;
  return (
    <div className="item" data-testid="item" data-item={itemId}>
      <div className="item__top">
        <button type="button" className="item__back" onClick={onBack ?? undefined} disabled={onBack === null} aria-label={intl.formatMessage({ id: "test.back" })} data-testid="back">
          ‹
        </button>
        <div className="item__progress">
          <ProgressBar value={index / count} label={intl.formatMessage({ id: "test.progressLabel" })} tone={useTone()} />
        </div>
        <span className="label" data-testid="progress">
          <FormattedMessage id="test.progress" values={{ index: index + 1, count }} />
        </span>
      </div>
      <p className="item__round" data-testid="round">
        <FormattedMessage id="test.round" values={{ round, rounds }} />
      </p>
      {instructionsKey === null ? null : (
        <p className="item__instructions" data-testid="instructions">
          <T k={instructionsKey} />
        </p>
      )}
      <p className="item__statement" id={statementId}>
        <T k={`items.${itemId}`} />
      </p>
      <div className="options" role="radiogroup" aria-labelledby={statementId}>
        {values.map((v, i) => {
          const dot = size.optionDotMin + Math.round((i * (size.optionDotMax - size.optionDotMin)) / Math.max(1, values.length - 1));
          return (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={selected === v}
              className="option"
              onClick={() => onAnswer(v)}
              data-value={v}
              aria-label={text(`${format.labels_key}.${v}`) ?? String(v)}
            >
              <span className="option__dot" style={{ width: dot, height: dot }} aria-hidden="true" />
              <span>
                <T k={`${format.labels_key}.${v}`} />
              </span>
              <span className="option__key" aria-hidden="true">
                {v}
              </span>
            </button>
          );
        })}
      </div>
      <button type="button" className="link-button" onClick={onLater}>
        <FormattedMessage id="test.later" />
      </button>
    </div>
  );
}
